import Scene from '../Scene';
import common from '../../common';
import Character from '../../Character';
import Text from '../../ui/Text';
import i18next from 'i18next';

export default class IntroScene extends Scene {
	constructor() {
		super();

		this._background = null;
		this._body = null;
		this._bodyText = null;
		this._page = 0;
		this._pageCount = 4;

		this._prevPageButton = null;
		this._nextPageButton = null;
		this._paginator = null;
		this._picture = null;
		this._languageChangeSignal = null;

		this._strings = {
			POPULATION: common.scenario.population.housed,
			TARGET: common.scenario.targets.housing,
			HOSPITALS: common.scenario.targets.hospital,
			SCHOOLS: common.scenario.targets.school,
			SCHOOL: common.scenario.targets.school > 1 ? i18next.t('gui.general.schools') : i18next.t('structure.school.description'),
			HOSPITAL: common.scenario.targets.hospital > 1 ? i18next.t('gui.general.hospitals') : i18next.t('structure.hospital.description'),
			HOTELS: common.scenario.targets.mission,
			BUDGET: common.numberWithCommas(common.scenario.budget.total),
			TIME: common.scenario.time / 60,
		};
	}

	init() {
		super.init();

		this.interactive = true;
		this.on('pointerup', () => {
			P3.audio.playSound('sfx_genericclick');
		});

		this._overlay = new PIXI.Sprite(PIXI.Texture.WHITE);
		this._overlay.alpha = 0.4;
		this._overlay.tint = 0x0;
		this._overlay.interactive = true;
		this.addChild(this._overlay);

		this._background = new PIXI.NineSlicePlane(P3.assets.texture('tooltip_tile_information_9slice'), 30, 30, 30, 30);
		this._background.width = 830;
		this._background.height = 500;
		this.addChild(this._background);

		const pointer = new PIXI.Sprite(P3.assets.texture('speech_bubble_pointer'));

		pointer.anchor.set(1, 0.5);
		pointer.position.set(20, 70);
		this._background.addChild(pointer);

		this._body = new PIXI.Container();
		this._body.position.set(20, 20);
		this._body.width = this._background.width - 40;
		this._body.height = this._background.height - 40;
		this._background.addChild(this._body);

		const text = i18next.t(`${common.selectedLevel}.mission.intro${this._page + 1}`, this._strings);

		this._bodyText = new Text(text, { font: '30px', align: 'left', tint: 0x0, maxWidth: this._background.width - 60 });
		this._bodyText.position.set(10, 5);
		this._body.addChild(this._bodyText);

		this._character = this._createAdvisor();
		this._character.scale.set(0.78);
		this._character.position.set((common.STAGE_WIDTH - P3.View.width + this._character.width) * 0.5, 120);
		this.addChild(this._character);

		this._background.position.set(this._character.x + this._character.width - 40, 50);

		this._createPageButtons();
		this._createPaginations(this._pageCount);
		this._updateIcon();

		this.resizeBackground();

		this._languageChangeSignal = common.languageManager.signals.changed.add(this.onLanguageChange, this);

		this._background.scale.set(0.8);
		new TWEEN.Tween(this._background.scale, this._tweens)
			.to({
				x: 1,
				y: 1,
			}, 300)
			.easing(TWEEN.Easing.Back.Out)
			.start();
	}

	_createAdvisor() {
		const avatar = new PIXI.Container();

		avatar.bg = new PIXI.Sprite(P3.assets.texture('character_bg'));
		avatar.bg.anchor.set(0.5);
		avatar.addChild(avatar.bg);

		avatar.fg = new PIXI.Sprite(P3.assets.texture('character_fg'));
		avatar.fg.anchor.set(0.5);
		avatar.addChild(avatar.fg);

		this._picture = new Character();
		this._picture.view.scale.set(0.5);
		this._picture.position.set(0, avatar.bg.height * 0.5);
		avatar.addChild(this._picture);
		this._picture.playAnimation('happy');
		this._picture.update();

		avatar.circleMask = new PIXI.Graphics();
		avatar.circleMask.position.set(avatar.bg.width * 0.5, avatar.bg.height * 0.5);
		avatar.circleMask.beginFill(0x0);
		avatar.circleMask.drawCircle(-(avatar.bg.width * 0.5), -(avatar.bg.height * 0.5), 74);
		avatar.circleMask.endFill();
		avatar.addChild(avatar.circleMask);
		this._picture.mask = avatar.circleMask;

		avatar.notificationIcon = new PIXI.Sprite(P3.assets.texture('btn_key_fact_new_calltoaction_out'));
		avatar.notificationIcon.anchor.set(0.5);
		avatar.notificationIcon.position.set(avatar.bg.width * 0.5 - 20, avatar.bg.height * 0.5 - 20);
		avatar.addChild(avatar.notificationIcon);

		avatar.notificationIcon.label = new Text('1', { font: '38px', fontWeight: 'bold', align: 'left', tint: 0xffffff });
		avatar.notificationIcon.label.anchor.set(0.5);
		avatar.notificationIcon.addChild(avatar.notificationIcon.label);

		avatar.notificationIcon.visible = false;

		avatar.scale.set(1.2);

		return avatar;
	}

	_createPageButtons() {
		let states = new P3.ButtonStates();

		states.normal = P3.assets.texture('btn_black_outline_9slice_out');
		states.over = P3.assets.texture('btn_black_outline_9slice_over');
		states.icon = P3.assets.texture('icon_arrow_r');

		this._prevPageButton = common.buttonFactory.createButton(states);
		this._prevPageButton.visible = false;
		this._prevPageButton.tint = 0x33415e;
		this._prevPageButton.scale.x = -1;
		this._prevPageButton.position.set(50, this._background.height - this._prevPageButton.height - 20);
		this._prevPageButton.signals.click.add(this._onPrevButtonClick, this);
		this._body.addChild(this._prevPageButton);

		this._prevPageButton.accessibleTitle = i18next.t('accessibility.button.previous');

		states = new P3.ButtonStates();
		states.normal = P3.assets.texture('btn_blue_fill_medium_9slice_out');
		states.over = P3.assets.texture('btn_blue_fill_medium_9slice_over');
		states.icon = P3.assets.texture('icon_arrow_r');

		this._nextPageButton = common.buttonFactory.createButton(states);
		this._nextPageButton.position.set(this._background.width - this._nextPageButton.width - 10, this._background.height - this._nextPageButton.height - 20);
		this._nextPageButton.signals.click.add(this._onNextButtonClick, this);
		this._body.addChild(this._nextPageButton);

		this._nextPageButton.accessibleTitle = i18next.t('accessibility.button.next');
	}

	_createPaginations(count) {
		this._paginator = new PIXI.Container();
		this._body.addChild(this._paginator);

		for (let i = 0; i < count; i++) {
			const icon = new PIXI.Sprite(P3.assets.texture('pagination_off'));

			icon.anchor.set(0.5);
			icon.position.set(i * 40, 0);
			this._paginator.addChild(icon);
		}

		this._paginator.position.set(this._background.width * 0.5 - this._paginator.width * 0.5, this._background.height - 90);
		this._paginator.children[0].texture = P3.assets.texture('pagination_on');
	}

	_onPrevButtonClick() {
		this._nextPageButton.visible = true;
		this._page = this._page === 0 ? this._page : this._page - 1;

		if (this._page === 0) {
			this._prevPageButton.visible = false;
		}

		this._updateIcon();
		const body = i18next.t(`${common.selectedLevel}.mission.intro${this._page + 1}`, this._strings);

		this._bodyText.text = body;

		this.resizeBackground();
	}

	_onNextButtonClick() {
		this._prevPageButton.visible = true;
		this._page = this._page === this._pageCount - 1 ? this._page : this._page + 1;

		if (this._page === this._pageCount - 1) {
			this._nextPageButton.on('pointertap', this._onPlayClick, this);
		}

		this._updateIcon();
		const body = i18next.t(`${common.selectedLevel}.mission.intro${this._page + 1}`, this._strings);

		this._bodyText.text = body;

		this.resizeBackground();
	}

	_updateIcon() {
		for (let i = 0; i < this._paginator.children.length; i++) {
			this._paginator.children[i].texture = P3.assets.texture('pagination_off');
			if (i === this._page) {
				this._paginator.children[i].texture = P3.assets.texture('pagination_on');
			}
		}
	}

	_onPlayClick() {
		this.signals.next.dispatch(this);
	}

	onLanguageChange() {
		this._strings.SCHOOL = common.scenario.targets.school > 1 ? i18next.t('gui.general.schools') : i18next.t('structure.school.description');
		this._strings.HOSPITAL = common.scenario.targets.hospital > 1 ? i18next.t('gui.general.hospitals') : i18next.t('structure.hospital.description');

		const body = i18next.t(`${common.selectedLevel}.mission.intro${this._page + 1}`, this._strings);

		this._bodyText.text = body;

		this.resizeBackground();
	}

	appear() {
		super.appear();
	}

	destroy() {
		common.languageManager.signals.changed.detach(this._languageChangeSignal, this);
		// clean up goes here
		super.destroy();
	}

	resizeBackground() {
		this._background.height = this._bodyText.height + 150;

		this._paginator.position.set(this._background.width * 0.5 - this._paginator.width * 0.5, this._background.height - 90);
		this._prevPageButton.position.set(50, this._background.height - this._prevPageButton.height - 20);
		this._nextPageButton.position.set(this._background.width - this._nextPageButton.width - 10, this._background.height - this._nextPageButton.height - 20);
	}

	resize() {
		super.resize();

		if (this._character) {
			this._character.x = (common.STAGE_WIDTH - P3.View.width + this._character.width + 40) * 0.5;
		}

		this._background.position.set(this._character.x + this._character.width - 40, 50);

		this._overlay.x = (common.STAGE_WIDTH - P3.View.width) * 0.5;
		this._overlay.width = P3.View.width;
		this._overlay.height = P3.View.height;
	}

	update() {
		super.update();

		this._picture.update();
	}
}
